<script>
import { mapGetters } from 'vuex';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  components: {
    ConversationBox,
    ConversationSidebar,
  },

  props: {
    conversationId: {
      type: [String, Number],
      required: true,
    },
  },

  emits: ['close'],

  setup() {
    const { uiSettings, updateUISettings } = useUISettings();

    return {
      uiSettings,
      updateUISettings,
    };
  },

  data() {
    return {
      isLoading: true,
      currentConversation: null,
      messages: [],
    };
  },

  computed: {
    ...mapGetters({
      conversations: 'getAllConversations',
      currentUser: 'getCurrentUser',
      uiSettings: 'getUISettings',
      // globalConfig: 'globalConfig/get', // Módulo não existe, comentado
      currentChat: 'getSelectedChat',
    }),

    conversationIdInt() {
      return parseInt(this.conversationId, 10);
    },

    conversation() {
      // Buscar na lista ou usar a conversa carregada
      return (
        this.currentConversation ||
        this.conversations.find(c => c.id === this.conversationIdInt)
      );
    },

    isAgentTyping() {
      const { conversation_typing_users: typingUsers } = this.uiSettings;
      return typingUsers?.[this.conversationIdInt];
    },

    shouldShowSidebar() {
      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen !== false;
    },
  },

  async mounted() {
    // Carregar a conversa com todas as dependências
    await this.loadConversation();

    // Nota: globalConfig e conversationAttachments foram comentados pois os módulos Vuex não existem
    // TODO: Implementar esses módulos se necessário no futuro

    // Adicionar listener de teclado
    document.addEventListener('keydown', this.handleKeyDown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);

    // Limpar conversa selecionada ao desmontar
    if (this.conversationIdInt) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  methods: {
    handleClose() {
      this.$emit('close');
    },

    handleKeyDown(event) {
      if (event.key === 'Escape' && !event.shiftKey && !event.ctrlKey) {
        this.handleClose();
      }
    },

    toggleSidebar() {
      const currentState = this.uiSettings.is_contact_sidebar_open;
      this.updateUISettings({
        is_contact_sidebar_open: !currentState,
      });
    },

    async markConversationAsRead() {
      if (!this.conversation || this.conversation.unread_count === 0) {
        return;
      }

      try {
        // Marcar como lida no backend silenciosamente
        await this.$store.dispatch('markMessagesRead', {
          id: this.conversationIdInt,
        });
      } catch (error) {
        console.error('Erro ao marcar conversa como lida:', error);
      }
    },

    async toggleStatus() {
      if (!this.conversation) return;

      try {
        const newStatus =
          this.conversation.status === 'open' ? 'resolved' : 'open';
        await this.$store.dispatch('toggleStatus', {
          conversationId: this.conversation.id,
          status: newStatus,
        });

        // Atualizar conversa local
        if (this.currentConversation) {
          this.currentConversation.status = newStatus;
        }
      } catch (error) {
        console.error('Erro ao alterar status:', error);
      }
    },

    async loadConversation() {
      try {
        this.isLoading = true;

        // Primeiro verificar se a conversa já está na lista
        let conversation = this.conversations.find(
          c => c.id === this.conversationIdInt
        );

        if (!conversation) {
          // Se não estiver, buscar do backend
          await this.$store.dispatch('getConversation', this.conversationIdInt);
          // Tentar encontrar novamente
          conversation = this.conversations.find(
            c => c.id === this.conversationIdInt
          );
        }

        if (conversation) {
          this.currentConversation = { ...conversation }; // Clonar para evitar reatividade

          // IMPORTANTE: Definir como conversa selecionada no store ANTES de renderizar
          await this.$store.dispatch('setActiveChat', {
            data: conversation,
          });

          // Garantir que o currentChat está definido no store
          await this.$store.commit('SET_CURRENT_CHAT_WINDOW', conversation);

          // Carregar agentes da inbox para permitir atribuições
          if (conversation.inbox_id) {
            await this.$store.dispatch('inboxAssignableAgents/fetch', [
              conversation.inbox_id,
            ]);
          }

          // Carregar informações da inbox
          if (conversation.inbox_id) {
            await this.$store.dispatch('inboxes/get');
          }

          // Aguardar um tick para garantir que tudo esteja pronto
          await this.$nextTick();

          // Carregar mensagens se necessário
          if (!conversation.messages || conversation.messages.length === 0) {
            try {
              await this.$store.dispatch('fetchPreviousMessages', {
                conversationId: this.conversationIdInt,
              });

              // Buscar conversa atualizada
              const updated = this.conversations.find(
                c => c.id === this.conversationIdInt
              );
              if (updated && updated.messages) {
                this.currentConversation = { ...updated };
                this.messages = updated.messages;
              }
            } catch (msgError) {
              console.error('Modal: Erro ao carregar mensagens:', msgError);
              // Continuar mesmo sem mensagens
            }
          } else {
            this.messages = conversation.messages;
          }

          // Carregar labels de forma segura
          if (this.conversationIdInt) {
            try {
              await this.$store.dispatch(
                'conversationLabels/get',
                this.conversationIdInt
              );
            } catch (labelError) {
              console.error('Modal: Erro ao carregar labels:', labelError);
            }
          }
        } else {
          console.error('Modal: Conversa não encontrada');
          throw new Error('Conversa não encontrada');
        }

        await this.$nextTick();

        // Marcar conversa como lida imediatamente ao abrir o modal
        this.markConversationAsRead();
      } catch (error) {
        console.error('Modal: Erro ao carregar conversa:', error);
        this.currentConversation = null;
        this.messages = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <!-- Modal Overlay -->
  <div
    class="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
    @click.self="handleClose"
  >
    <!-- Modal Container - Grande e espaçoso -->
    <div
      class="relative w-[95vw] h-[90vh] max-w-[1600px] bg-color-background dark:bg-slate-900 rounded-xl shadow-2xl overflow-hidden flex flex-col"
    >
      <!-- Header do Modal -->
      <div
        class="flex items-center justify-between px-6 py-3 border-b border-color-border dark:border-slate-700 bg-white dark:bg-slate-800"
      >
        <div class="flex items-center gap-3">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Conversa #{{ conversationId }}
          </h2>
          <span
            v-if="conversation"
            class="px-2 py-1 rounded-full text-xs font-medium"
            :class="[
              conversation.status === 'open'
                ? 'bg-blue-100 text-blue-700'
                : conversation.status === 'resolved'
                  ? 'bg-blue-100 text-blue-700'
                  : conversation.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-slate-100 text-slate-700',
            ]"
          >
            {{ conversation.status }}
          </span>
          <span v-if="conversation?.inbox" class="text-sm text-slate-500">
            <fluent-icon icon="tray" size="16" class="mr-1" />
            {{ conversation.inbox.name }}
          </span>
        </div>

        <div class="flex items-center gap-2">
          <!-- Espaço removido - botões movidos para dentro da conversa -->

          <!-- Fechar -->
          <button
            class="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center"
            title="Fechar (ESC)"
            @click="handleClose"
          >
            <i class="i-lucide-x text-base" />
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex-1 flex items-center justify-center">
        <div class="text-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-woot-500 mx-auto mb-4"
          />
          <p class="text-slate-500">Carregando conversa...</p>
        </div>
      </div>

      <!-- Conteúdo Principal -->
      <div
        v-else-if="conversation && conversation.id === conversationIdInt"
        class="flex-1 flex overflow-hidden"
      >
        <!-- Área de Conversa com ConversationBox -->
        <div class="flex-1 flex flex-col relative">
          <ConversationBox
            v-if="conversation && conversation.id"
            :inbox-id="conversation.inbox_id"
            :is-contact-panel-open="shouldShowSidebar"
            :is-on-expanded-layout="false"
            :is-inbox-view="false"
            class="flex-1"
          >
            <slot />
          </ConversationBox>

          <!-- Botão de toggle da sidebar com ícone de contato e seta indicadora -->
          <div class="absolute top-14 right-4 z-50">
            <button
              v-tooltip.left="
                shouldShowSidebar ? 'Ocultar contatos' : 'Mostrar contatos'
              "
              class="p-2 rounded-lg shadow-sm transition-all duration-200 flex items-center gap-1"
              :class="
                shouldShowSidebar
                  ? 'bg-woot-500 text-white hover:bg-woot-600'
                  : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              "
              @click="toggleSidebar"
            >
              <i class="i-ph-user-bold text-base" />
              <i
                :class="
                  shouldShowSidebar
                    ? 'i-lucide-chevron-right'
                    : 'i-lucide-chevron-left'
                "
                class="text-sm transition-transform duration-200"
              />
            </button>
          </div>
        </div>

        <!-- Sidebar usando componente nativo -->
        <ConversationSidebar
          v-if="shouldShowSidebar && conversation"
          :current-chat="conversation"
        />
      </div>

      <!-- Erro ou conversa não encontrada -->
      <div v-else class="flex-1 flex items-center justify-center">
        <div class="text-center">
          <fluent-icon icon="warning" size="48" class="-400 mb-4" />
          <p
            class="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            Não foi possível carregar a conversa
          </p>
          <p class="text-sm text-slate-500 mb-4">ID: {{ conversationId }}</p>
          <button
            class="px-4 py-2 bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors"
            @click="loadConversation"
          >
            <fluent-icon icon="arrow-sync" size="16" class="mr-2" />
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Transição da sidebar */
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: all 0.3s ease;
}

.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Garantir altura completa para os componentes */
:deep(.conversation-panel) {
  height: 100% !important;
  position: relative;
}

:deep(.conversations-sidebar) {
  height: 100% !important;
}

:deep(.messages-list) {
  height: 100% !important;
}

:deep(.conversation-wrap) {
  height: 100% !important;
}

/* Remover botão de voltar do header completamente */
:deep(.back-button),
:deep(.conversation-header .back-button),
:deep([class*='back-button']),
:deep([class*='BackButton']) {
  display: none !important;
}

/* Modal principal tem z-index: 999 */
/* Garantir que TODOS os modais de mídia abram na frente */
:deep(.modal-container),
:deep(.image-modal),
:deep(.woot-modal__backdrop),
:deep(.woot-modal),
:deep(.modal),
:deep(.media-lightbox),
:deep(.lightbox-modal),
:deep(.image-lightbox) {
  z-index: 9999 !important;
}

:deep(.modal-mask),
:deep(.modal-backdrop),
:deep(.media-backdrop) {
  z-index: 9998 !important;
}

/* Forçar visualizador de imagens e anexos para frente */
:deep(.image-view-modal),
:deep(.media-modal),
:deep(.attachment-preview),
:deep(.media-gallery),
:deep(.file-preview-modal),
:deep(.image-preview-modal),
:deep(.attachment-modal),
:deep(.file-modal),
:deep(.woot-modal--image) {
  z-index: 10000 !important;
  position: fixed !important;
}

/* Garantir que o conteúdo da imagem fique visível */
:deep(.modal-content),
:deep(.image-content),
:deep(.media-content) {
  z-index: 10001 !important;
}

/* Override específico para componentes do Chatwoot */
:deep(.conversation-panel .modal),
:deep(.conversation-panel .woot-modal),
:deep(.messages-list .modal),
:deep(.messages-list .woot-modal),
:deep([class*='ImageView']),
:deep([class*='FilePreview']),
:deep([class*='AttachmentPreview']) {
  z-index: 10002 !important;
  position: fixed !important;
}

/* Garantir que o overlay das mídias também fique na frente */
:deep(.overlay),
:deep(.modal-overlay),
:deep(.image-overlay) {
  z-index: 9997 !important;
}

/* Garantir que áudio players funcionem corretamente */
:deep(audio) {
  width: 100%;
  max-width: 300px;
}

:deep(.audio-player) {
  cursor: pointer;
}

/* Sidebar sections com collapse/expand funcional */
:deep(.accordion-item) {
  transition: all 0.3s ease;
}

:deep(.accordion-item__header) {
  cursor: pointer;
  user-select: none;
}

:deep(.accordion-item__content) {
  transition: max-height 0.3s ease;
}

/* Corrigir posição do emoji picker para abrir para cima */
:deep(.emoji-dialog) {
  bottom: 100% !important;
  top: auto !important;
  right: auto !important;
  left: 0 !important;
  margin-bottom: 8px !important;
}

:deep(.emoji-picker) {
  bottom: 100% !important;
  top: auto !important;
  right: auto !important;
  left: 0 !important;
}

:deep(.emoji-picker__wrapper) {
  bottom: 100% !important;
  top: auto !important;
}

/* Garantir que o emoji picker fique visível acima do input */
:deep(.reply-box .emoji-dialog),
:deep(.reply-box .emoji-picker) {
  position: absolute !important;
  bottom: calc(100% + 10px) !important;
  top: auto !important;
  z-index: 1000 !important;
}

/* Ajustar a seta do emoji picker para apontar para baixo */
:deep(.emoji-dialog::after),
:deep(.emoji-picker::after) {
  content: '';
  position: absolute;
  top: 100%;
  left: 20px;
  border: 8px solid transparent;
  border-top-color: white;
  margin-top: -1px;
}

/* Garantir que o picker não seja cortado */
:deep(.conversation-wrap) {
  overflow: visible !important;
}

:deep(.reply-box__top) {
  position: relative;
  overflow: visible !important;
}

/* Animação de entrada do modal */
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.relative {
  animation: modalFadeIn 0.2s ease-out;
}

/* Responsividade */
@media (max-width: 768px) {
  .w-\[380px\] {
    width: 100%;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
  }

  .relative {
    width: 100vw;
    height: 100vh;
    max-width: 100%;
    border-radius: 0;
  }
}
</style>
